/**
 * 考试模块共享常量
 * 与 exam_papers / questions 表字段对齐
 */

// ─── 学段 (stage) ───────────────────────────────────────────────

export const STAGES = [
  { value: 'primary', label: '小学' },
  { value: 'junior', label: '初中' },
  { value: 'senior', label: '高中' },
] as const
export type Stage = (typeof STAGES)[number]['value']

export const STAGE_LABELS: Record<Stage, string> = {
  primary: '小学',
  junior: '初中',
  senior: '高中',
}

// ─── 学科 (subject) ─────────────────────────────────────────────

export const SUBJECTS: Record<Stage, { value: string; label: string }[]> = {
  primary: [
    { value: '语文', label: '语文' },
    { value: '数学', label: '数学' },
    { value: '英语', label: '英语' },
  ],
  junior: [
    { value: '语文', label: '语文' },
    { value: '数学', label: '数学' },
    { value: '英语', label: '英语' },
    { value: '物理', label: '物理' },
    { value: '化学', label: '化学' },
    { value: '生物', label: '生物' },
    { value: '道法', label: '道法' },
    { value: '历史', label: '历史' },
    { value: '地理', label: '地理' },
  ],
  senior: [
    { value: '语文', label: '语文' },
    { value: '数学', label: '数学' },
    { value: '英语', label: '英语' },
    { value: '物理', label: '物理' },
    { value: '化学', label: '化学' },
    { value: '生物', label: '生物' },
    { value: '政治', label: '政治' },
    { value: '历史', label: '历史' },
    { value: '地理', label: '地理' },
  ],
}

export const ALL_SUBJECTS = [...new Set(Object.values(SUBJECTS).flat().map(s => s.label))]

// ─── 年级 (grade) ───────────────────────────────────────────────

export const GRADES: Record<Stage, { value: string; label: string }[]> = {
  primary: [
    { value: '一年级', label: '一年级' },
    { value: '二年级', label: '二年级' },
    { value: '三年级', label: '三年级' },
    { value: '四年级', label: '四年级' },
    { value: '五年级', label: '五年级' },
    { value: '六年级', label: '六年级' },
  ],
  junior: [
    { value: '初一', label: '初一' },
    { value: '初二', label: '初二' },
    { value: '初三', label: '初三' },
  ],
  senior: [
    { value: '高一', label: '高一' },
    { value: '高二', label: '高二' },
    { value: '高三', label: '高三' },
  ],
}

// ─── 考试分类 (category) ────────────────────────────────────────

export const CATEGORIES = [
  '校内',
  '竞赛',
]

// ─── 校内考试类型 (examType) ─────────────────────────────────────

export const EXAM_TYPES = [
  '单元测试',
  '期中考试',
  '期末考试',
  '月考',
  '模拟考试',
  '专项练习',
  '分班考',
  '小升初',
  '中考',
  '高考',
  '竞赛',
  '其他',
]

/** 按学段显示的升学考试类型 */
export const GRADUATION_EXAM_TYPES: Record<Stage, string[]> = {
  primary: ['小升初'],
  junior: ['中考'],
  senior: ['高考'],
}

// ─── 题型 (question.type) ───────────────────────────────────────

export const QUESTION_TYPES = [
  { value: 'choice', label: '单选题' },
  { value: 'multi_choice', label: '多选题' },
  { value: 'fill_blank', label: '填空题' },
  { value: 'true_false', label: '判断题' },
  { value: 'essay', label: '解答题' },
  { value: 'proof', label: '证明题' },
  // 英语专用 (复合题 — 存在 question_groups.type 上)
  { value: 'reading_comp', label: '阅读理解' },
  { value: 'cloze', label: '完形填空' },
  { value: 'listening', label: '听力' },
  { value: 'seven_choose_five', label: '七选五' },
  { value: 'grammar_fill', label: '语法填空' },
  { value: 'matching', label: '信息匹配' },
  { value: 'error_correction', label: '短文改错' },
  { value: 'writing', label: '写作' },
  // 数学专用
  { value: 'puzzle', label: '数阵图' },
]

// ─── 难度 (difficulty) ──────────────────────────────────────────

export const DIFFICULTY_LEVELS = [
  { value: 1, label: '基础' },
  { value: 2, label: '简单' },
  { value: 3, label: '中等' },
  { value: 4, label: '较难' },
  { value: 5, label: '困难' },
]

// ─── 年份 ───────────────────────────────────────────────────────

export const YEARS = [
  '2026', '2025', '2024', '2023', '2022', '2021', '2020',
  '2019', '2018', '2017', '2016', '2015', '2014', '2013',
  '2012', '2011', '2010', '2009', '2008', '2007', '2006', '2005',
]

// ─── 地区层级数据 ────────────────────────────────────────────────

export interface RegionItem {
  name: string
  children?: RegionItem[]
}

export const REGIONS: RegionItem[] = [
  {
    name: '北京市',
    children: [
      { name: '东城区' },
      { name: '西城区' },
      { name: '朝阳区' },
      { name: '丰台区' },
      { name: '石景山区' },
      { name: '海淀区' },
      { name: '门头沟区' },
      { name: '房山区' },
      { name: '通州区' },
      { name: '顺义区' },
      { name: '昌平区' },
      { name: '大兴区' },
      { name: '怀柔区' },
      { name: '平谷区' },
      { name: '密云区' },
      { name: '延庆区' },
    ],
  },
  {
    name: '天津市',
    children: [
      { name: '和平区' },
      { name: '河东区' },
      { name: '河西区' },
      { name: '南开区' },
      { name: '河北区' },
      { name: '红桥区' },
      { name: '东丽区' },
      { name: '西青区' },
      { name: '津南区' },
      { name: '北辰区' },
      { name: '武清区' },
      { name: '宝坻区' },
      { name: '滨海新区' },
    ],
  },
  {
    name: '上海市',
    children: [
      { name: '黄浦区' },
      { name: '徐汇区' },
      { name: '长宁区' },
      { name: '静安区' },
      { name: '普陀区' },
      { name: '虹口区' },
      { name: '杨浦区' },
      { name: '闵行区' },
      { name: '宝山区' },
      { name: '嘉定区' },
      { name: '浦东新区' },
      { name: '金山区' },
      { name: '松江区' },
      { name: '青浦区' },
      { name: '奉贤区' },
      { name: '崇明区' },
    ],
  },
  {
    name: '江苏省',
    children: [
      { name: '南京市' },
      { name: '无锡市' },
      { name: '徐州市' },
      { name: '常州市' },
      { name: '苏州市' },
      { name: '南通市' },
      { name: '连云港市' },
      { name: '淮安市' },
      { name: '盐城市' },
      { name: '扬州市' },
      { name: '镇江市' },
      { name: '泰州市' },
      { name: '宿迁市' },
    ],
  },
  {
    name: '浙江省',
    children: [
      { name: '杭州市' },
      { name: '宁波市' },
      { name: '温州市' },
      { name: '嘉兴市' },
      { name: '湖州市' },
      { name: '绍兴市' },
      { name: '金华市' },
      { name: '衢州市' },
      { name: '舟山市' },
      { name: '台州市' },
      { name: '丽水市' },
    ],
  },
  {
    name: '广东省',
    children: [
      { name: '广州市' },
      { name: '韶关市' },
      { name: '深圳市' },
      { name: '珠海市' },
      { name: '汕头市' },
      { name: '佛山市' },
      { name: '江门市' },
      { name: '湛江市' },
      { name: '茂名市' },
      { name: '肇庆市' },
      { name: '惠州市' },
      { name: '梅州市' },
      { name: '汕尾市' },
      { name: '河源市' },
      { name: '阳江市' },
      { name: '清远市' },
      { name: '东莞市' },
      { name: '中山市' },
      { name: '潮州市' },
      { name: '揭阳市' },
      { name: '云浮市' },
    ],
  },
  {
    name: '四川省',
    children: [
      { name: '成都市' },
      { name: '自贡市' },
      { name: '攀枝花市' },
      { name: '泸州市' },
      { name: '德阳市' },
      { name: '绵阳市' },
      { name: '广元市' },
      { name: '遂宁市' },
      { name: '内江市' },
      { name: '乐山市' },
      { name: '南充市' },
      { name: '眉山市' },
      { name: '宜宾市' },
      { name: '广安市' },
      { name: '达州市' },
      { name: '雅安市' },
      { name: '巴中市' },
      { name: '资阳市' },
    ],
  },
  {
    name: '重庆市',
    children: [
      { name: '重庆市' },
    ],
  },
  {
    name: '湖南省',
    children: [
      { name: '长沙市' },
      { name: '株洲市' },
      { name: '湘潭市' },
      { name: '衡阳市' },
      { name: '邵阳市' },
      { name: '岳阳市' },
      { name: '常德市' },
      { name: '张家界市' },
      { name: '益阳市' },
      { name: '郴州市' },
      { name: '永州市' },
      { name: '怀化市' },
      { name: '娄底市' },
    ],
  },
  {
    name: '湖北省',
    children: [
      { name: '武汉市' },
      { name: '黄石市' },
      { name: '十堰市' },
      { name: '宜昌市' },
      { name: '襄阳市' },
      { name: '鄂州市' },
      { name: '荆门市' },
      { name: '孝感市' },
      { name: '荆州市' },
      { name: '黄冈市' },
      { name: '咸宁市' },
      { name: '随州市' },
    ],
  },
  {
    name: '山东省',
    children: [
      { name: '济南市' },
      { name: '青岛市' },
      { name: '淄博市' },
      { name: '枣庄市' },
      { name: '东营市' },
      { name: '烟台市' },
      { name: '潍坊市' },
      { name: '济宁市' },
      { name: '泰安市' },
      { name: '威海市' },
      { name: '日照市' },
      { name: '临沂市' },
      { name: '德州市' },
      { name: '聊城市' },
      { name: '滨州市' },
      { name: '菏泽市' },
    ],
  },
  {
    name: '河南省',
    children: [
      { name: '郑州市' },
      { name: '开封市' },
      { name: '洛阳市' },
      { name: '平顶山市' },
      { name: '安阳市' },
      { name: '鹤壁市' },
      { name: '新乡市' },
      { name: '焦作市' },
      { name: '濮阳市' },
      { name: '许昌市' },
      { name: '漯河市' },
      { name: '三门峡市' },
      { name: '南阳市' },
      { name: '商丘市' },
      { name: '信阳市' },
      { name: '周口市' },
      { name: '驻马店市' },
    ],
  },
  {
    name: '福建省',
    children: [
      { name: '福州市' },
      { name: '厦门市' },
      { name: '莆田市' },
      { name: '三明市' },
      { name: '泉州市' },
      { name: '漳州市' },
      { name: '南平市' },
      { name: '龙岩市' },
      { name: '宁德市' },
    ],
  },
  {
    name: '陕西省',
    children: [
      { name: '西安市' },
      { name: '铜川市' },
      { name: '宝鸡市' },
      { name: '咸阳市' },
      { name: '渭南市' },
      { name: '延安市' },
      { name: '汉中市' },
      { name: '榆林市' },
      { name: '安康市' },
      { name: '商洛市' },
    ],
  },
  {
    name: '云南省',
    children: [
      { name: '昆明市' },
      { name: '曲靖市' },
      { name: '玉溪市' },
      { name: '保山市' },
      { name: '昭通市' },
      { name: '丽江市' },
      { name: '普洱市' },
      { name: '临沧市' },
    ],
  },
]

// ─── 筛选用的学段-学科-年级结构 ──────────────────────────────────

export interface SchoolLevel {
  id: string
  name: string
  subjects: { value: string; label: string }[]
  grades: { value: string; label: string }[]
}

export const SCHOOL_LEVELS: SchoolLevel[] = STAGES.map((stage) => ({
  id: stage.value,
  name: stage.label,
  subjects: SUBJECTS[stage.value],
  grades: GRADES[stage.value],
}))
